import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';
import InputField from '../../components/InputField';
import ValueFormatters from '../valueFormatters';
import { ValidatorRule } from '../../validator/validatorRule.constants';
import { EmailFieldValidator } from '../../validator/emailFieldValidator';
import { TEXT_TO_LOWERCASE } from '../../constants/styles';
import { EmailDomainSuggestion } from './EmailDomainSuggestion';

interface IEmailFieldProps {
    autoFocus?: boolean;
    id?: string;
    labelId?: string;
    intl: InjectedIntl;
    disabled?: boolean;
    value?: string;
    required?: boolean;
    qaId?: string;
    validatorRules?: ValidatorRule[];
    currentEmail?: string;
    skipValidator?: boolean;
}

class EmailField extends React.Component<IEmailFieldProps> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly MAX_LENGTH: number;
    private emailFieldValidator: EmailFieldValidator;
    state = {
        hasFocus: false,
        hasBlur: false,
    };

    constructor(props: IEmailFieldProps) {
        super(props);

        const {
            id,
            labelId,
            validatorRules = [ValidatorRule.EmailLengthRule, ValidatorRule.EUEmailRule],
        } = props;

        this.ID = id ? id : 'email';
        this.LABEL_ID = labelId ? labelId : 'label.email';
        this.MAX_LENGTH = 128;
        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
        this.emailFieldValidator = new EmailFieldValidator(validatorRules);
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): IErrorMessage {
        const { intl, required, currentEmail, skipValidator = false } = this.props;
        if (!value && required) {
            return TranslationHelpers.getMessageDescriptor('error.new_email_missing');
        }

        let errorMessage: IErrorMessage = '';
        if (value && !skipValidator) {
            const err = this.emailFieldValidator.validate(value, this.LABEL_ID);
            if (err !== null) {
                errorMessage = err.message;
            }

            if (currentEmail && value.toLocaleLowerCase() === currentEmail.toLocaleLowerCase()) {
                return TranslationHelpers.getMessageDescriptor('error.new_email.no_current_email');
            }
        }
        return errorMessage;
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, handleBlur },
    }: FieldProps): ReactNode {
        const { autoFocus, disabled, value, required, qaId } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;

        const onBlur = (event: React.FocusEvent<any>) => {
            setFieldValue(this.ID, ValueFormatters.trimExtraSpaces(event.target.value));
            handleBlur(event);
            this.setState({ hasBlur: true, hasFocus: false });
        };

        const onFocus = () => {
            this.setState({ hasFocus: true });
        };

        return (
            <InputField
                autoComplete="email"
                labelMessage={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                type="text"
                id={this.ID}
                name={this.ID}
                value={value}
                style={TEXT_TO_LOWERCASE}
                error={errorMessage}
                autoFocus={autoFocus}
                required={required}
                maxLength={this.MAX_LENGTH}
                disabled={disabled}
                data-qa-id={`${qaId}-field`}
                inputMode="email"
                onBlur={onBlur}
                onFocus={onFocus}
                convert={false}
            >
                {isTouched && this.state.hasBlur && !this.state.hasFocus && (
                    <EmailDomainSuggestion value={value} />
                )}
            </InputField>
        );
    }
}

export default injectIntl(EmailField);
